import { useEffect, useRef, useState } from 'react';
import type { Mood } from '@reels/shared';
import { MUSIC_PREVIEWS } from '../lib/music-previews';
import { t } from '../i18n';

type MusicMood = Mood | 'auto';

/** A new mood or track unmounts its audio, so it cannot keep playing offscreen. */
export function MusicPreview({ mood }: { mood: MusicMood }) {
  return <MusicPlaylist key={mood} mood={mood} />;
}

function MusicPlaylist({ mood }: { mood: MusicMood }) {
  const tracks = mood === 'auto' ? MUSIC_PREVIEWS : MUSIC_PREVIEWS.filter(track => track.mood === mood);
  const [index, setIndex] = useState(0);
  const track = tracks[index];
  if (!track) return null;
  return <MusicPlayer key={track.url} track={track} next={tracks.length > 1 ? () => setIndex(i => (i + 1) % tracks.length) : undefined} />;
}

function MusicPlayer({ track, next }: { track: (typeof MUSIC_PREVIEWS)[number]; next?: () => void }) {
  const previewUrl = `${import.meta.env.BASE_URL}${track.url.replace(/^\/+/, '')}`;
  const audio = useRef<HTMLAudioElement>(null);
  const attempt = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  useEffect(() => {
    const player = audio.current;
    // Restore the source after React StrictMode's setup/cleanup/setup cycle.
    if (player) player.setAttribute('src', previewUrl);
    const onOtherMedia = (event: Event) => { if (event.target !== player) player?.pause(); };
    const onHidden = () => { if (document.hidden) player?.pause(); };
    document.addEventListener('play', onOtherMedia, true);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      attempt.current++;
      document.removeEventListener('play', onOtherMedia, true);
      document.removeEventListener('visibilitychange', onHidden);
      if (player) { player.pause(); player.removeAttribute('src'); player.load(); }
    };
  }, [previewUrl]);
  const stop = () => { attempt.current++; audio.current?.pause(); setLoading(false); setPlaying(false); };
  const toggle = () => {
    const player = audio.current;
    if (!player) return;
    if (playing || loading) { stop(); return; }
    setError(''); setLoading(true);
    if (player.error) player.load();
    if (player.ended) player.currentTime = 0;
    const current = ++attempt.current;
    // Called directly in the user gesture: works with mobile autoplay restrictions.
    void player.play().catch(() => {
      if (current === attempt.current) { setError(t('components.music.playFailed')); setPlaying(false); }
    }).finally(() => { if (current === attempt.current) setLoading(false); });
  };
  return <section className="music-preview" aria-label={t('components.music.aria')}>
    <audio ref={audio} src={previewUrl} preload="none" aria-hidden="true"
      onPlay={() => {
        document.querySelectorAll<HTMLMediaElement>('audio, video').forEach(media => { if (media !== audio.current) media.pause(); });
        setPlaying(true);
      }}
      onPlaying={() => setLoading(false)} onWaiting={event => { if (!event.currentTarget.paused) setLoading(true); }}
      onPause={() => { attempt.current++; setPlaying(false); setLoading(false); }}
      onEnded={() => { setPlaying(false); setLoading(false); setElapsed(0); }}
      onTimeUpdate={event => setElapsed(Math.min(track.durationS, event.currentTarget.currentTime))}
      onError={() => { setPlaying(false); setLoading(false); setError(t('components.music.unavailable')); }} />
    <div className="music-preview-heading">
      <div className="col grow"><strong>{track.title}</strong><span className="small muted">{t('components.music.sample', { artist: track.artist })}</span></div>
      <span className="small muted">0:{String(Math.floor(elapsed)).padStart(2, '0')} / 0:15</span>
    </div>
    <progress max={track.durationS} value={elapsed} aria-label={t('components.music.playbackAria')} />
    <div className="music-preview-actions">
      <button type="button" className="btn" aria-label={playing || loading ? t('components.music.stopAria') : t('components.music.listen')} onClick={toggle}>
        {loading ? t('components.music.loading') : playing ? t('components.music.pause') : t('components.music.listen')}
      </button>
      {next && <button type="button" className="btn ghost" onClick={next}>{t('components.music.another')}</button>}
    </div>
    {error && <p className="small" role="alert">{error}</p>}
    <p className="small muted">{t('components.music.auto')}</p>
    <a className="music-preview-source small muted" href={track.sourceUrl} target="_blank" rel="noreferrer">{t('components.music.source', { license: track.license })}</a>
  </section>;
}
